import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthTokenInitialState, AuthTokenTypes } from './authToken.state.ts';
import { cognitoLogin, verifyToken } from 'store/authToken/authToken.thunk.ts';
import QueryState from '@/common/enum/QueryState.ts';
import SitePermissionTypes from '@/common/enum/SitePermissionTypes.ts';
import { Nullable } from 'interfaces/Nullable.ts';
import { IUserPortal } from 'interfaces/UserAccountInfo.interface.ts';

const initialState: AuthTokenTypes = AuthTokenInitialState;

export const authTokenSlice = createSlice({
  name: 'authToken',
  initialState,
  reducers: {
    setAuthCreds: (
      state,
      action: PayloadAction<{
        id: string;
        userId: number;
        accessType: SitePermissionTypes;
        username: string;
      }>
    ) => {
      state.token = action.payload.id;
      state.userId = action.payload.userId;
      state.accessType = action.payload.accessType;
      state.username = action.payload.username;
    },
    setAuthToken: (state, action) => {
      state.token = action.payload;
    },
    setSessionExpiration: (state, action: PayloadAction<Nullable<number>>) => {
      state.tokenExpiresAt = action.payload;
    },
    // The portal hand-off carries its ID token in the URL rather than in a
    // Cognito session, so that JWT is the only one such a user will ever have.
    setJwtToken: (state, action: PayloadAction<string>) => {
      state.jwtToken = action.payload;
    },
    // Applied after a silent Cognito refresh: the account object and the raw ID
    // token are re-read from the same new token, so the opaque token, the ID
    // token, the verification payload and the expiry all move together and
    // cannot drift apart.
    setRefreshedSession: (
      state,
      action: PayloadAction<{
        token: string;
        jwtToken: string;
        tokenVerification: IUserPortal;
        expiresAt: Nullable<number>;
      }>
    ) => {
      state.token = action.payload.token;
      state.jwtToken = action.payload.jwtToken;
      state.tokenVerification = action.payload.tokenVerification;
      state.tokenExpiresAt = action.payload.expiresAt;
      state.error = null;
    },
    setErrorMsg: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    resetAuthToken: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyToken.pending, (state) => {
        state.queryState = QueryState.AWAIT;
        state.error = null;
        state.token = null;
        state.tokenVerification = null;
        state.tokenExpiresAt = null;
        state.jwtToken = null;
      })
      .addCase(verifyToken.fulfilled, (state, action: any) => {
        state.queryState = QueryState.READY;
        if (action.payload) state.tokenVerification = action.payload;
      })
      .addCase(verifyToken.rejected, (state, action) => {
        state.queryState = QueryState.FAIL;
        state.error = action.error.message ?? 'Something went wrong';
        state.tokenVerification = null;
      })
      .addCase(cognitoLogin.pending, (state) => {
        state.queryState = QueryState.AWAIT;
        state.error = null;
        state.token = null;
        state.tokenVerification = null;
        state.tokenExpiresAt = null;
        state.jwtToken = null;
      })
      // The username/password path is the one entry point that never sees an
      // `?id_token=` parameter, so the raw ID token has to be taken from the
      // authenticated Cognito session here. Both halves come from one token, so
      // the account and the credential always describe the same session.
      .addCase(cognitoLogin.fulfilled, (state, action) => {
        state.queryState = QueryState.READY;
        if (action.payload) {
          state.tokenVerification = action.payload.account;
          state.jwtToken = action.payload.jwtToken;
        }
      })
      .addCase(cognitoLogin.rejected, (state, action) => {
        state.queryState = QueryState.FAIL;
        state.error = action.error.message ?? 'Something went wrong';
        state.tokenVerification = null;
      });
  },
});
export const { resetAuthToken, setSessionExpiration, setRefreshedSession, setJwtToken } =
  authTokenSlice.actions;
export default authTokenSlice.reducer;
